import React from "react";
import styled from "styled-components";
import { FaHeart, FaPen, FaMicrophone, FaUsers } from "react-icons/fa";
import {
  colors,
  fonts,
  spacing,
  borderRadius,
  shadows,
  transitions,
} from "../styles/branding";

const AboutContainer = styled.div`
  padding-top: 80px;
`;

const HeroSection = styled.section`
  background: ${colors.gradient.primary};
  color: white;
  padding: 4rem 2rem;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  font-family: ${fonts.heading};
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  opacity: 0.9;
  max-width: 650px;
  margin: 0 auto;
  font-family: ${fonts.body};
`;

const ContentSection = styled.section`
  padding: 3rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TextContent = styled.div`
  h2 {
    font-size: 2.5rem;
    margin-bottom: 1rem;
    color: ${colors.inkBlack};
    font-family: ${fonts.heading};
    font-weight: 600;
  }

  p {
    font-size: 1.1rem;
    line-height: 1.5;
    color: ${colors.coolTaupe};
    margin-bottom: 1rem;
    font-family: ${fonts.body};
  }

  a {
    color: ${colors.dustyRose};
    text-decoration: underline;
    text-decoration-color: ${colors.dustyRose};
    text-decoration-thickness: 1px;
    transition: all 0.3s ease;

    &:hover {
      color: ${colors.coolTaupe};
      text-decoration-color: ${colors.coolTaupe};
    }
  }
`;

const ImageContainer = styled.div`
  img {
    width: 100%;
    border-radius: ${borderRadius.md};
    box-shadow: ${shadows.lg};
  }
`;

const ValuesSection = styled.section`
  padding: 3rem 2rem;
  background: ${colors.paleMauve};
`;

const ValuesGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 3rem;
`;

const ValueCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: ${borderRadius.md};
  text-align: center;
  box-shadow: ${shadows.md};
`;

const ValueIcon = styled.div`
  font-size: 3rem;
  color: ${colors.dustyRose};
  margin-bottom: 1rem;
`;

const ValueTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: ${colors.inkBlack};
  font-family: ${fonts.heading};
  font-weight: 500;
`;

const ValueText = styled.p`
  color: ${colors.coolTaupe};
  line-height: 1.6;
  font-family: ${fonts.body};
`;

const JourneyTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 2rem;
  color: ${colors.inkBlack};
  font-family: ${fonts.heading};
  font-weight: 600;
`;

const About = () => {
  return (
    <AboutContainer>
      <HeroSection>
        <HeroTitle>About Shana Marie Roark</HeroTitle>
        <HeroSubtitle>
          Poet, entrepreneur, and founder of Poem Club, based in New York City
        </HeroSubtitle>
      </HeroSection>

      <ContentSection>
        <TwoColumnLayout>
          <TextContent>
            <h2>Shana's Story</h2>
            <p>
              Shana Roark, also known as <i>SuperGirlReject</i>, is a poet,
              conceptual artist, and founder of Poem Club.
            </p>
            <p>
              In 2019, she began Custom Poetry, a living conceptual art
              performance in which she sits on sidewalks and writes poems for
              strangers, on demand.
            </p>
            <p>Loneliness, she says, was the catalyst.</p>
            <p>“I was new to the city, and I felt alone,” she says.</p>
            <p>
              “I was tired of all the vapid conversations, so I thought, maybe
              if I offer people a poem, they’ll open up. Maybe we can talk about
              things that matter. Things they couldn’t say otherwise.”
            </p>
            <p>
              She made a sign: <i>Pick a Subject, Get a Poem</i>.
            </p>
            <p>What happened next changed her life.</p>
            <p>
              “I became a witness to people’s darkest days. Their secrets. Their
              joy. Their heartbreak. Their most treasured stories.
              <br />
              And I tried to capture what they gave me, translate their truth
              into a poem, into art.”
            </p>
            <p>
              “At first, I thought I was helping them. But eventually, I
              realized, they were healing me. The interactions were so raw, so
              real, it was addictive. I didn’t even want to go home. They gave
              me their story. I gave them a poem. A piece of my heart.”
            </p>
            <p>
              Since then, Shana has written thousands of custom poems,
              performing at events like Art Basel, the Whitney Biennial, and the
              MoMA. Her work has reached millions online, with over 70,000
              followers and videos viewed more than 10 million times. She’s been
              featured in{" "}
              <a
                href="https://nypost.com/2025/05/03/us-news/nyc-street-poet-left-teaching-to-pursue-dream-grateful-every-day/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i>The New York Post</i>
              </a>
              , which chronicled her journey to making a six-figure income from
              poetry.
            </p>
            <p>
              Her books—<a href="/books">becoming</a>,{" "}
              <a href="/books">half-here</a>, and{" "}
              <a href="/books">love is real</a>—have sold thousands of copies to
              readers around the world.
            </p>
            <p>
              Shana’s next big dream is to teach other artists how to make a
              full-time living from their work, as she has done.
            </p>
            <p>
              She currently lives in New York City and continues to perform at
              private and corporate events—and her favorite stage of all:
            </p>
            <p>The streets of Manhattan.</p>
          </TextContent>
          <ImageContainer>
            <img src="/images/about/main.jpg" alt="Shana Marie Roark" />
          </ImageContainer>
        </TwoColumnLayout>
      </ContentSection>

      <ValuesSection>
        <JourneyTitle>What Shana Believes</JourneyTitle>
        <ValuesGrid>
          <ValueCard>
            <ValueIcon>
              <FaHeart />
            </ValueIcon>
            <ValueTitle>Authentic Connection</ValueTitle>
            <ValueText>
              Shana believes in the power of authentic human connection. Her
              poetry creates bridges between hearts, allowing readers to see
              themselves in her words and find comfort in shared experiences.
            </ValueText>
          </ValueCard>

          <ValueCard>
            <ValueIcon>
              <FaPen />
            </ValueIcon>
            <ValueTitle>Healing Through Words</ValueTitle>
            <ValueText>
              Words have the power to heal, transform, and inspire. Through her
              poetry, Shana aims to create spaces where readers can process
              their emotions and find strength in vulnerability.
            </ValueText>
          </ValueCard>

          <ValueCard>
            <ValueIcon>
              <FaMicrophone />
            </ValueIcon>
            <ValueTitle>Voice for Others</ValueTitle>
            <ValueText>
              Shana uses her voice to amplify the stories of others,
              particularly those who may feel unheard or unseen. Her work
              celebrates the diversity of human experience and the beauty of our
              shared humanity.
            </ValueText>
          </ValueCard>

          <ValueCard>
            <ValueIcon>
              <FaUsers />
            </ValueIcon>
            <ValueTitle>Community Building</ValueTitle>
            <ValueText>
              Through readings, workshops, and speaking engagements, Shana
              creates communities where people can come together to share,
              learn, and grow through the power of poetry and storytelling.
            </ValueText>
          </ValueCard>
        </ValuesGrid>
      </ValuesSection>
    </AboutContainer>
  );
};

export default About;
